import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from 'components/form/Button';
import { InputWithStyle } from 'components/form/Input';
import { TabContent } from 'components/layouts/TabComponent';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import useUserStore from 'state/userStore';
import { toast } from 'react-toastify';
import { updateUser } from 'api/user';

const UserSchema = z.object({
  fullName: z.string().min(3, 'Name must be at least 3 characters'),
  email: z.string().email(),
  dob: z.string(),
  city: z.string(),
  province: z.string(),
  country: z.string(),
  zipCode: z.string(),
  phoneNumber: z.string(),
  address: z.string(),
});

type UserSchemaType = z.infer<typeof UserSchema>;

interface ProfileTabProps {
  currentTab: string;
}

const ProfileTab = ({ currentTab }: ProfileTabProps) => {
  //   const authToken = window.localStorage.getItem('token');

  const { user, setUser } = useUserStore();
  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, dirtyFields },
  } = useForm<UserSchemaType>({
    resolver: zodResolver(UserSchema),
    mode: 'onTouched',
    defaultValues: {
      ...user,
      dob: user?.dob || '',
    },
  });

  const [loading, setLoading] = useState(false);

  const updateUserFunction = async (data: Partial<UserSchemaType>) => {
    setLoading(true);
    try {
      const res = await updateUser(data);

      if (res?.data) {
        toast.success('profile updated');
        setUser(res.data);
      }
    } catch (error: any) {
      console.log(error);
      const { errors } = error.response.data;
      errors.map((err: { error: string }) => {
        return toast.error(err.error);
      });
      console.log(error);
    }
    setLoading(false);
  };

  const onSubmit = (data: Partial<UserSchemaType>) => {
    const requestData: any = {};

    const keys = Object.keys(dirtyFields);
    for (const field of keys) {
      const value = data[field as keyof typeof data];
      requestData[field] = value;
    }

    updateUserFunction(requestData);
  };
  return (
    <TabContent tabId="tab1" currentTab={currentTab}>
      <form className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <label className="space-y-2">
          <div className="flex items-center space-x-2">
            <p className="font-bold capitalize">Full name</p>
          </div>
          <InputWithStyle
            startIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                />
              </svg>
            }
            type="text"
            name="fullName"
            placeholder="full name"
            required
            control={control}
          />
        </label>
        <label className="space-y-2">
          <div className="flex items-center space-x-2">
            <p className="font-bold capitalize">Email</p>
          </div>
          <InputWithStyle
            disabled
            startIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
            }
            type="email"
            name="email"
            placeholder="email"
            required
            control={control}
          />
        </label>
        <label className="space-y-2">
          <div className="flex items-center space-x-2">
            <p className="font-bold capitalize">Phone number</p>
          </div>
          <InputWithStyle
            startIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                />
              </svg>
            }
            type="number"
            name="phoneNumber"
            placeholder="phone number"
            required
            control={control}
          />
        </label>
        <label className="space-y-2">
          <div className="flex items-center space-x-2">
            <p className="font-bold capitalize">Date of Birth</p>
          </div>
          <InputWithStyle
            startIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z"
                />
              </svg>
            }
            type="date"
            name="dob"
            placeholder="Date of Birth"
            required
            control={control}
          />
        </label>
        <label className="space-y-2">
          <div className="flex items-center space-x-2">
            <p className="font-bold capitalize">Country</p>
          </div>
          <InputWithStyle
            disabled
            startIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M20.893 13.393l-1.135-1.135a2.252 2.252 0 01-.421-.585l-1.08-2.16a.414.414 0 00-.663-.107.827.827 0 01-.812.21l-1.273-.363a.89.89 0 00-.738 1.595l.587.39c.59.395.674 1.23.172 1.732l-.2.2c-.212.212-.33.498-.33.796v.41c0 .409-.11.809-.32 1.158l-1.315 2.191a2.11 2.11 0 01-1.81 1.025 1.055 1.055 0 01-1.055-1.055v-1.172c0-.92-.56-1.747-1.414-2.089l-.655-.261a2.25 2.25 0 01-1.383-2.46l.007-.042a2.25 2.25 0 01.29-.787l.09-.15a2.25 2.25 0 012.37-1.048l1.178.236a1.125 1.125 0 001.302-.795l.208-.73a1.125 1.125 0 00-.578-1.315l-.665-.332-.091.091a2.25 2.25 0 01-1.591.659h-.18c-.249 0-.487.1-.662.274a.931.931 0 01-1.458-1.137l1.411-2.353a2.25 2.25 0 00.286-.76m11.928 9.869A9 9 0 008.965 3.525m11.928 9.868A9 9 0 118.965 3.525"
                />
              </svg>
            }
            type="text"
            name="country"
            placeholder="country"
            required
            control={control}
          />
        </label>
        <label className="space-y-2">
          <div className="flex items-center space-x-2">
            <p className="font-bold capitalize">State/Province</p>
          </div>
          <InputWithStyle
            startIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M20.893 13.393l-1.135-1.135a2.252 2.252 0 01-.421-.585l-1.08-2.16a.414.414 0 00-.663-.107.827.827 0 01-.812.21l-1.273-.363a.89.89 0 00-.738 1.595l.587.39c.59.395.674 1.23.172 1.732l-.2.2c-.212.212-.33.498-.33.796v.41c0 .409-.11.809-.32 1.158l-1.315 2.191a2.11 2.11 0 01-1.81 1.025 1.055 1.055 0 01-1.055-1.055v-1.172c0-.92-.56-1.747-1.414-2.089l-.655-.261a2.25 2.25 0 01-1.383-2.46l.007-.042a2.25 2.25 0 01.29-.787l.09-.15a2.25 2.25 0 012.37-1.048l1.178.236a1.125 1.125 0 001.302-.795l.208-.73a1.125 1.125 0 00-.578-1.315l-.665-.332-.091.091a2.25 2.25 0 01-1.591.659h-.18c-.249 0-.487.1-.662.274a.931.931 0 01-1.458-1.137l1.411-2.353a2.25 2.25 0 00.286-.76m11.928 9.869A9 9 0 008.965 3.525m11.928 9.868A9 9 0 118.965 3.525"
                />
              </svg>
            }
            type="text"
            name="province"
            placeholder="state"
            required
            control={control}
          />
        </label>
        <label className="space-y-2">
          <div className="flex items-center space-x-2">
            <p className="font-bold capitalize">Address</p>
          </div>
          <InputWithStyle
            startIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                />
              </svg>
            }
            type="text"
            name="address"
            placeholder="address"
            required
            control={control}
          />
        </label>
        <label className="space-y-2">
          <div className="flex items-center space-x-2">
            <p className="font-bold capitalize">Zip code</p>
          </div>
          <InputWithStyle
            startIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                />
              </svg>
            }
            type="text"
            name="zipCode"
            placeholder="zip code"
            required
            control={control}
          />
        </label>
        <Button
          className="ml-auto rounded-md bg-blue-500 px-3 py-2 text-white md:col-span-2"
          disabled={!isDirty || !isValid || loading}
          loading={loading}
          onClick={handleSubmit(onSubmit)}
        >
          update
        </Button>
      </form>
    </TabContent>
  );
};

export default ProfileTab;
